import {
    AddThreadDTO,
    Author,
    Kudos,
    KudosRecipients,
    KudosType,
    LinkPreview,
    ManagerCommentDTO,
    MoodValue,
    Reaction,
    Reactions,
    RichTextComment,
    Thread,
    ThreadAuthor,
    Threads,
    TimelineItemType,
    TimelinePollResultTimelineItemType,
    TopicType
} from "@teammood/timeline";
import { PollResult } from "../timeline/timeline-item/poll-result/poll-result.type";
import {
    ClosedRecurrencePollResults,
    PollUnderCreation,
    RecurrencePollResults,
    RecurrenceType
} from "../polls/create-poll/create-poll.type";
import { ITeam } from "@teammood/team";
import { CompositeFilter } from "@teammood/filter";
import { KudosCreationDTO } from "../timeline/daily-overview/days.service";
import { TranslateService } from "@ngx-translate/core";

export class Taskbook {
    error?: string;
    tasks: Task[];
    managers: TaskAssignee[]
}

export abstract class Task {
    error?: string;
    date: Date;
    id: string;
    labels: TaskLabel[];
    team: {
        id: string;
        name: string;
    };
    itemType: TimelineItemType;
    reactions: Reaction[];
    threads: Thread[];
    threadAuthors: ThreadAuthor[];
    reviews: Review[];
    assignees: TaskAssignee[];
    availableAssignees: TaskAssignee[];

    public static createTask(task: Task): Task {
        task['reviews'] = task.reviews.map(r => Review.createReview(r));

        switch (task.itemType) {
            case "kudosItem":
                return Object.assign(task, new KudosTask());
            case "moodItem":
                return Object.assign(task, new MoodTask());
            case "scaleQuestionResultItem":
                return Object.assign(task, new ScalePollResultTask());
            case "topicItem":
                return Object.assign(task, new TopicTask());
            case "badMoodPassItem":
                return Object.assign(task, new BadMoodStreakTask());
            case "badMoodStreakItem":
                return Object.assign(task, new BadMoodStreakTask());
            case "agreeDisagreeQuestionResultItem":
                return Object.assign(task, new AgreeDisagreePollResultTask());
            case "closedQuestionResultItem":
                return Object.assign(task, new ClosedPollResultTask());
            case "fiveStarsQuestionResultItem":
                return Object.assign(task, new FiveStarsPollResultTask());
            default:
                throw new Error("Unknown task type: " + task.itemType);
        }

    }
}

export type ReviewType = 'ArchiveReview' | 'AddThreadReview' | 'PostQuestionReview' | 'SendKudosReview' | 'ModerateReview' | 'KeepReview' | 'ReadReview' | 'ManagerCommentReview' | 'NewThreadReview' | 'MentionedReview' | 'FinishedQuestionReview' | 'CreatedFromOtherItemReview' | 'AssignmentReview' | 'UnassignmentReview';

export abstract class Review {
    id: string;
    date: string;
    reviewer: Reviewer;
    reviewType: ReviewType;
    icon: string;

    questionId?: string;
    occurrenceId?: string;
    kudosId?: string;
    comment?: any;
    isDeletable?: boolean;
    isEditable?: boolean;
    createdFromTimelineItemId?: string;
    createdFromTimelineItemType?: string;
    assignees?: TaskAssignee[];
    unassignees?: TaskAssignee[];

    abstract text(userId: string, translateService: TranslateService): string

    protected reviewerIsYou(userId: string) {
        return this.reviewer.id === userId;
    }

    static createReview(review: Review): Review {
        switch (review.reviewType) {
            case "ArchiveReview":
                return new ArchiveReview(review);
            case "KeepReview":
                return new KeepReview(review);
            case "ReadReview":
                return new ReadReview(review);
            case "AddThreadReview":
                return new AddThreadReview(review);
            case "PostQuestionReview":
                return new PostQuestionReview(review);
            case "SendKudosReview":
                return new SendKudosReview(review);
            case "ModerateReview":
                return new ModerateReview(review);
            case "ManagerCommentReview":
                return new ManagerCommentReview(review);
            case "NewThreadReview":
                return new NewThreadReview(review);
            case "MentionedReview":
                return new MentionedReview(review);
            case "FinishedQuestionReview":
                return new FinishedQuestionReview(review);
            case "CreatedFromOtherItemReview":
                return new CreatedFromOtherItemReview(review);
            case "AssignmentReview":
                return new AssignmentReview(review);
            case "UnassignmentReview":
                return new UnassignmentReview(review);
            default:
                throw new Error("Unknown review type: " + review.reviewType);
        }
    }
}

export class ArchiveReview extends Review {

    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'ArchiveReview';
    override icon: string = 'fa-box-archive';

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
    }

    text(userId: string, translateService: TranslateService): string {
        return this.reviewerIsYou(userId) ? translateService.instant('task_review.you_archive') : translateService.instant('task_review.archive');
    }
}

export class KeepReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'KeepReview';
    override icon: string = 'fa-bookmark';

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
    }

    text(userId: string, translateService: TranslateService): string {
        return this.reviewerIsYou(userId) ? translateService.instant('task_review.you_keep') : translateService.instant('task_review.keep');
    }
}

export class ReadReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'ReadReview';
    override icon: string = 'fa-check';

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
    }

    text(userId: string, translateService: TranslateService): string {
        return '';
    }
}

export class AddThreadReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'AddThreadReview';
    override icon: string = 'fa-comment';

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
    }

    text(userId: string, translateService: TranslateService): string {
        return this.reviewerIsYou(userId) ? translateService.instant('task_review.you_discuss') : translateService.instant('task_review.discuss');
    }
}

export class PostQuestionReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'PostQuestionReview';
    override icon: string = 'fa-square-poll-horizontal';

    override questionId: string;

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
        this.questionId = review.questionId;
    }

    text(userId: string, translateService: TranslateService): string {
        return this.reviewerIsYou(userId) ? translateService.instant('task_review.you_poll') : translateService.instant('task_review.poll');
    }
}

export class SendKudosReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'SendKudosReview';
    override icon: string = 'fa-stars';

    override kudosId: string;

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
        this.kudosId = review.kudosId;
    }

    text(userId: string, translateService: TranslateService): string {
        return this.reviewerIsYou(userId) ? translateService.instant('task_review.you_kudos') : translateService.instant('task_review.kudos');
    }
}

export class ModerateReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'ModerateReview';
    override icon: string = 'fa-message-slash';

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
    }

    text(userId: string, translateService: TranslateService): string {
        return this.reviewerIsYou(userId) ? translateService.instant('task_review.you_moderate') : translateService.instant('task_review.moderate');
    }
}

export class ManagerCommentReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'ManagerCommentReview';
    override icon: string = 'fa-comment-pen';

    override comment: any;
    override isDeletable: boolean;
    override isEditable: boolean;

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
        this.comment = review.comment;
        this.isDeletable = review.isDeletable;
        this.isEditable = review.isEditable;
    }

    text(userId: string, translateService: TranslateService): string {
        return this.comment;
    }
}

export class NewThreadReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'NewThreadReview';
    override icon: string = 'fa-comments';

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
    }

    text(userId: string, translateService: TranslateService): string {
        return this.reviewerIsYou(userId) ? translateService.instant('task_review.you_new_thread') : translateService.instant('task_review.new_thread');
    }
}

export class MentionedReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'MentionedReview';
    override icon: string = 'fa-comment-pen';

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
    }

    text(userId: string, translateService: TranslateService): string {
        return this.reviewerIsYou(userId) ? translateService.instant('task_review.you_were_mentioned') : translateService.instant('task_review.has_been_mentioned');
    }
}

export class FinishedQuestionReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'FinishedQuestionReview';
    override icon: string = 'fa-square-poll-horizontal';

    override questionId: string;
    override occurrenceId: string;

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
        this.questionId = review.questionId;
        this.occurrenceId = review.occurrenceId;
    }

    text(userId: string, translateService: TranslateService): string {
        return this.reviewerIsYou(userId) ? translateService.instant('task_review.you_poll_finished') : translateService.instant('task_review.poll_finished');
    }
}

export class CreatedFromOtherItemReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'CreatedFromOtherItemReview';
    override icon: string = 'fa-square-poll-horizontal';

    override createdFromTimelineItemId: string;
    override createdFromTimelineItemType: string;

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
        this.createdFromTimelineItemId = review.createdFromTimelineItemId;
        this.createdFromTimelineItemType = review.createdFromTimelineItemType;
    }

    text(userId: string, translateService: TranslateService): string {
        return this.reviewerIsYou(userId) ? translateService.instant('task_review.you_poll_created_from_other_item') : translateService.instant('task_review.poll_created_from_other_item');
    }
}

export class AssignmentReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'AssignmentReview';
    override icon: string = 'fa-arrow-right-from-line';

    override assignees: TaskAssignee[];

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
        this.assignees = review.assignees;
    }

    text(userId: string, translateService: TranslateService): string {

        const isAssignedToYou = this.assignees.find(assignee => assignee.id === userId);
        if (isAssignedToYou) {

            if (this.assignees.length === 1) {
                return (this.reviewerIsYou(userId) ? translateService.instant('task_review.you_assigned') : translateService.instant('task_review.assigned_to_you')) + ' ' +
                    ' ' + translateService.instant('task_review.you');
            } else {
                return (this.reviewerIsYou(userId) ? translateService.instant('task_review.you_assigned') : translateService.instant('task_review.assigned_to_you')) + ' ' +
                    this.assignees
                        .filter(assignee => assignee.id !== userId)
                        .map(assignee => `${assignee.firstname} ${assignee.lastname}`)
                        .join(', ') + ' ' + translateService.instant('task_review.and_you');
            }
        } else {
            return (this.reviewerIsYou(userId) ? translateService.instant('task_review.you_assigned') : translateService.instant('task_review.assigned_to_you')) + ' ' +
                this.assignees
                    .map(assignee => `${assignee.firstname} ${assignee.lastname}`)
                    .join(', ');
        }

    }
}

export class UnassignmentReview extends Review {
    override date: string;
    override id: string;
    override reviewer: Reviewer;
    override reviewType: ReviewType = 'UnassignmentReview';
    override icon: string = 'fa-arrow-left-from-line';

    override unassignees: TaskAssignee[];

    constructor(review: Review) {
        super();
        this.date = review.date;
        this.id = review.id;
        this.reviewer = review.reviewer;
        this.unassignees = review.unassignees;
    }

    text(userId: string, translateService: TranslateService): string {

        const isAssignedToYou = this.unassignees.find(assignee => assignee.id === userId);
        if (isAssignedToYou) {

            if (this.unassignees.length === 1) {
                return (this.reviewerIsYou(userId) ? translateService.instant('task_review.you_unassigned') : translateService.instant('task_review.unassigned_to_you')) + ' ' +
                    ' ' + translateService.instant('task_review.you');
            } else {
                return (this.reviewerIsYou(userId) ? translateService.instant('task_review.you_unassigned') : translateService.instant('task_review.unassigned_to_you')) + ' ' +
                    this.unassignees
                        .filter(assignee => assignee.id !== userId)
                        .map(assignee => `${assignee.firstname} ${assignee.lastname}`)
                        .join(', ') + ' ' + translateService.instant('task_review.and_you');
            }
        } else {
            return (this.reviewerIsYou(userId) ? translateService.instant('task_review.you_unassigned') : translateService.instant('task_review.unassigned_to_you')) + ' ' +
                this.unassignees
                    .map(assignee => `${assignee.firstname} ${assignee.lastname}`)
                    .join(', ');
        }

    }
}

export interface Reviewer{
    id: string;
    fullname: string;
}

export interface TeamIdNameSize {
    id: string;
    name: string;
}

export class MoodTask implements Task, Reactions, Threads {
    date: Date;
    id: string;
    labels: TaskLabel[];
    assignees: TaskAssignee[];
    team: TeamIdNameSize;
    itemType: TimelineItemType = 'moodItem';
    reactions: Reaction[];
    threads: Thread[];
    threadAuthors: ThreadAuthor[];
    mood: MoodValue;
    comment: {
        body: string;
        linkPreviews: LinkPreview[];
    };
    reviews: Review[];
    availableAssignees: TaskAssignee[];
}

export class BadMoodStreakTask implements Task, Reactions, Threads {
    date: Date;
    id: string;
    labels: TaskLabel[];
    assignees: TaskAssignee[];
    team: TeamIdNameSize;
    reactions: Reaction[];
    threads: Thread[];
    itemType: TimelineItemType = "badMoodStreakItem";
    badMoodCount: number;
    threadAuthors: ThreadAuthor[];
    reviews: Review[];
    availableAssignees: TaskAssignee[];
}

export class KudosTask implements Task, Reactions, Threads {
    date: Date;
    id: string;
    labels: TaskLabel[];
    assignees: TaskAssignee[];
    team: TeamIdNameSize;
    reactions: Reaction[];
    threads: Thread[];
    itemType: TimelineItemType = "kudosItem";
    threadAuthors: ThreadAuthor[];
    message:  {
        body: string;
        linkPreviews: LinkPreview[];
    };
    recipients: KudosRecipients;
    kudosType?: KudosType;
    author?: Author;
    reviews: Review[];
    availableAssignees: TaskAssignee[];
}

export class TopicTask implements Task, Reactions, Threads {
    date: Date;
    id: string;
    labels: TaskLabel[];
    assignees: TaskAssignee[];
    team: TeamIdNameSize;
    reactions: Reaction[];
    threads: Thread[];
    itemType: TimelineItemType = "topicItem";
    threadAuthors: ThreadAuthor[];
    title: {
        body: string;
        linkPreviews: LinkPreview[];
    };
    type: TopicType;
    reviews: Review[];
    availableAssignees: TaskAssignee[];
}

export interface PollResultTask extends Task, Reactions, Threads {

    pollTargets?: ITeam[]
    recurrence?: Recurrence;
    allResults?: RecurrencePollResults | ClosedRecurrencePollResults;
    isRecurring?: boolean;
}

export class AgreeDisagreePollResultTask implements PollResultTask, Reactions, Threads {
    date: Date;
    id: string;
    labels: TaskLabel[];
    assignees: TaskAssignee[];
    team: TeamIdNameSize;
    reactions: Reaction[];
    threads: Thread[];
    threadAuthors: ThreadAuthor[];
    itemType: TimelinePollResultTimelineItemType = "agreeDisagreeQuestionResultItem";
    author: Author;
    label: string;
    results: PollResult;
    reviews: Review[];
    pollTargets?: ITeam[]
    recurrence?: Recurrence;
    isRecurring?: boolean;
    availableAssignees: TaskAssignee[];
}

export class ClosedPollResultTask implements Task, Reactions, Threads {
    date: Date;
    id: string;
    labels: TaskLabel[];
    assignees: TaskAssignee[];
    team: TeamIdNameSize;
    reactions: Reaction[];
    threads: Thread[];
    threadAuthors: ThreadAuthor[];
    itemType: TimelinePollResultTimelineItemType = "closedQuestionResultItem";
    author: Author;
    label: string;
    results: PollResult;
    reviews: Review[];
    pollTargets?: ITeam[]
    recurrence?: Recurrence;
    allResults?: ClosedRecurrencePollResults; 
    isRecurring?: boolean;
    availableAssignees: TaskAssignee[];
}

export class ScalePollResultTask implements Task, Reactions, Threads {
    date: Date;
    id: string;
    labels: TaskLabel[];
    assignees: TaskAssignee[];
    team: TeamIdNameSize;
    reactions: Reaction[];
    threads: Thread[];
    threadAuthors: ThreadAuthor[];
    itemType: TimelinePollResultTimelineItemType = "scaleQuestionResultItem";
    author: Author;
    label: string;
    results: PollResult;
    reviews: Review[];
    pollTargets?: ITeam[]
    recurrence?: Recurrence;
    isRecurring?: boolean;
    availableAssignees: TaskAssignee[];
}
export class FiveStarsPollResultTask implements Task, Reactions, Threads {
    date: Date;
    id: string;
    labels: TaskLabel[];
    assignees: TaskAssignee[];
    team: TeamIdNameSize;
    reactions: Reaction[];
    threads: Thread[];
    threadAuthors: ThreadAuthor[];
    itemType: TimelinePollResultTimelineItemType = "fiveStarsQuestionResultItem";
    author: Author;
    label: string;
    results: PollResult;
    pollTargets?: ITeam[]
    reviews: Review[];
    recurrence?: Recurrence;
    isRecurring?: boolean;
    availableAssignees: TaskAssignee[];
}

export type TaskAction = {
    id: TaskActionId;
    label: {
        title: string;
        description: string;
    };
    class: string;
    icon: string;
    operableInDialog: boolean;
    createDTO: (task: Task, filter: CompositeFilter, opt1?, opt2?) => TaskActionDTO,
}

export enum TaskActionsEnum {
    thread = 'thread', 
    kudos = 'kudos',
    makePollrecurrent = 'makePollrecurrent',
    poll = 'poll',
    report = 'report',
    archive = 'archive',
    support = 'support', // thread fo streak : todo
    keep = 'keep',
    skip = 'archskipive',
}

export type TaskActionId = keyof typeof TaskActionsEnum;

export interface Recurrence {
    frequency: RecurrenceType;
    duration: number;
    pollTargets: string[];
    enabled: boolean; 
}

export interface TaskActionContentDTO {
    id: string;
}

export interface TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: TaskActionContentDTO;
    filter: CompositeFilter;
}
export class ArchiveTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: TaskActionContentDTO = {
        id: "archive"
    };
    filter: CompositeFilter;
    constructor(entityId: string, entityType: TimelineItemType, filter: CompositeFilter) {
        this.entityId = entityId;
        this.entityType = entityType;
        this.filter = filter;
    }
}

export class ReadTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: TaskActionContentDTO = {
        id: "read"
    };
    filter: CompositeFilter;
    constructor(entityId: string, entityType: TimelineItemType, filter: CompositeFilter) {
        this.entityId = entityId;
        this.entityType = entityType;
        this.filter = filter;
    }
}
export class KeepTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: KeepContentDTO;
    filter: CompositeFilter;
    constructor(entityId: string, task: Task, filter: CompositeFilter) {
        this.entityId = entityId;
        this.entityType = task.itemType;
        this.filter = filter;
        this.taskActionContent = new KeepContentDTO(task.assignees, task.labels)
    }
}
export class KeepContentDTO implements TaskActionContentDTO {
    id: string = "keep";
    labels: TaskLabel[]
    assignees: TaskAssignee[]

    constructor( assignees: TaskAssignee[], labels: TaskLabel[]) {
        this.assignees = assignees;
        this.labels = labels;
    }
  }

export class AddLabelTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: AddLabelContentDTO;
    filter: CompositeFilter;
    constructor(entityId: string, task: Task, labelsToAdd: TaskLabel[], filter: CompositeFilter) {
        this.entityId = entityId;
        this.entityType = task.itemType;
        this.filter = filter;
        this.taskActionContent = new AddLabelContentDTO(labelsToAdd)
    }
}
export class AddLabelContentDTO implements TaskActionContentDTO {
    id: string = "addLabel";
    labels: TaskLabel[]
    constructor(labelsToAdd: TaskLabel[]) {
        this.labels = labelsToAdd;
    }
  }

export class RemoveLabelTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: RemoveLabelContentDTO;
    filter: CompositeFilter;
    constructor(entityId: string, task: Task, labelsToRemove: TaskLabel[], filter: CompositeFilter) {
        this.entityId = entityId;
        this.entityType = task.itemType;
        this.filter = filter;
        this.taskActionContent = new RemoveLabelContentDTO(labelsToRemove)
    }
}
export class RemoveLabelContentDTO implements TaskActionContentDTO {
    id: string = "removeLabel";
    labels: TaskLabel[]
    constructor(labelsToRemove: TaskLabel[]) {
        this.labels = labelsToRemove;
    }
}

export class AddAssigneeTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: AddAssigneeContentDTO;
    filter: CompositeFilter;
    constructor(entityId: string, task: Task, assigneesToAdd: TaskAssignee[], filter: CompositeFilter) {
        this.entityId = entityId;
        this.entityType = task.itemType;
        this.filter = filter;
        this.taskActionContent = new AddAssigneeContentDTO(assigneesToAdd)
    }
}
export class AddAssigneeContentDTO implements TaskActionContentDTO {
    id: string = "addAssignee";
    assignees: TaskAssignee[]
    constructor(assigneesToAdd: TaskAssignee[]) {
        this.assignees = assigneesToAdd;
    }
  }

export class RemoveAssigneeTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: RemoveAssigneeContentDTO;
    filter: CompositeFilter;
    constructor(entityId: string, task: Task, assigneesToRemove: TaskAssignee[], filter: CompositeFilter) {
        this.entityId = entityId;
        this.entityType = task.itemType;
        this.filter = filter;
        this.taskActionContent = new RemoveAssigneeContentDTO(assigneesToRemove)
    }
}
export class RemoveAssigneeContentDTO implements TaskActionContentDTO {
    id: string = "removeAssignee";
    assignees: TaskAssignee[]
    constructor(assigneesToRemove: TaskAssignee[]) {
        this.assignees = assigneesToRemove;
    }
}

export class UpdateLabelTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: UpdateLabelContentDTO;
    filter: CompositeFilter;
    constructor(entityId: string, task: Task, newLabels: TaskLabel[], filter: CompositeFilter) {
        this.entityId = entityId;
        this.entityType = task.itemType;
        this.filter = filter;
        this.taskActionContent = new UpdateLabelContentDTO(newLabels)
    }
}
export class UpdateLabelContentDTO implements TaskActionContentDTO {
    id: string = "updateLabels";
    labels: TaskLabel[]
    constructor(newLabels: TaskLabel[]) {
        this.labels = newLabels;
    }
  }

export class AddThreadTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: AddThreadDTO;
    filter: CompositeFilter;
    constructor(timelineItemId: string, entityType: TimelineItemType, filter: CompositeFilter, message: RichTextComment, isAnonymous: boolean) {
        this.entityId = timelineItemId;
        this.entityType = entityType;
        this.filter = filter;
        this.taskActionContent = new AddThreadDTO(timelineItemId, entityType, message, isAnonymous);
    }
}

export class ManagerCommentTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: ManagerCommentDTO;
    filter: CompositeFilter;
    constructor(timelineItemId: string, entityType: TimelineItemType, filter: CompositeFilter, message: RichTextComment, isAnonymous: boolean) {
        this.entityId = timelineItemId;
        this.entityType = entityType;
        this.filter = filter;
        this.taskActionContent = new ManagerCommentDTO(timelineItemId, entityType, message, isAnonymous);
    }
}


export class CreateKudosTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: KudosCreationDTO;
    filter: CompositeFilter;
    constructor(entityId: string, entityType: TimelineItemType, filter: CompositeFilter, kudos: Kudos, teamId: string) {
        this.entityId = entityId;
        this.entityType = entityType;
        this.filter = filter;
        this.taskActionContent = new KudosCreationDTO(kudos, teamId);
    }
}

export class CreatePollTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: PollUnderCreation;
    filter: CompositeFilter;
    constructor(entityId: string, entityType: TimelineItemType, filter: CompositeFilter, question: PollUnderCreation) {
        this.entityId = entityId;
        this.entityType = entityType;
        this.filter = filter;
        this.taskActionContent = this.format(question);
    }

    private format(question: PollUnderCreation){
        let n =1;
        question.answerOptions.choices = question.answerOptions.choices.map(choice => {
            return {
                label: choice.label,
                position: n++,
            }
        })
        return question;
    }
}

export class MakePollRecurrentTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: RecurrenceDTO;
    filter: CompositeFilter;
    constructor(entityId: string, entityType: TimelineItemType, filter: CompositeFilter, recurrence: Recurrence, teamIds: string[]) {
        this.entityId = entityId;
        this.entityType = entityType;
        this.filter = filter;
        this.taskActionContent = new RecurrenceDTO(recurrence, teamIds);
    }
}

export class ModerationTaskActionDTO implements TaskActionDTO {
    entityId: string;
    entityType: TimelineItemType;
    taskActionContent: ModerationDTO;
    filter: CompositeFilter;
    constructor(entityId: string, entityType: TimelineItemType, filter: CompositeFilter, why: string) {
        this.entityId = entityId;
        this.entityType = entityType;
        this.filter = filter;
        this.taskActionContent = new ModerationDTO(why);
    }
}

class RecurrenceDTO implements TaskActionContentDTO{
    id: string = 'makePollrecurrent';
    frequency: string;
    duration: number;
    pollIds: string[];
    constructor(recurrence: Recurrence, teamIds: string[]) {
        this.frequency = recurrence.frequency;
        this.duration = recurrence.duration;
        this.pollIds = teamIds;
    }}

class ModerationDTO implements TaskActionContentDTO{
    id: string = 'moderate';
    reason: string;
    constructor(reason: string){
        this.reason = reason;
    }
}

export class TaskListItem {
    task: Task;
    isSelected: boolean;
    listContent: { // will be comment or title or message...
        body: string;
    };
    mood?: { value: MoodValue; iconPath: string; };
    results?: PollResult;

    constructor(task: Task) {

        this.task = Task.createTask(task);
        this.listContent = { body: "" };
        this.isSelected = false;
    }

    toggleSelection(){
        this.isSelected = !this.isSelected;
    }

    isRead(userId: string): boolean {
        if (this.task.reviews) {
            const myReviews = this.task.reviews.filter(r => r.reviewer.id === userId);
            if (myReviews.length <= 1){
                return false; // just kept, check
            }
            if (myReviews[myReviews.length - 1].reviewType === 'ReadReview'){
                return true; // your read review, done
            }else{
                return myReviews[myReviews.length - 1].reviewer.id === userId;
            }
        }
        return false;
    }

    getLastUpdate(): Review{
        // rules (ordered by older to newer):
        // 1. if there is only one review, return it -> should be a keepReview
        // 2. if there is more than one review, return the last one that is not a readReview or that is not done by the user (not read)
        if(this.task.reviews?.length === 1){
            return this.task.reviews[0];
        }else{
            const reviewsWithoutRead = this.task.reviews.filter(review => review.reviewType !== 'ReadReview');
            return reviewsWithoutRead[reviewsWithoutRead.length - 1];
        }
    }
}

export interface TaskLabel {
    id: string;
    name: string;
    color: string;
    labeler?: Reviewer // who put the label on the task
}

export interface TaskAssignee {
    id: string;
    firstname: string;
    lastname: string;
}

